import { cloneDeep } from 'lodash';
import {
  asyncScheduler,
  buffer,
  catchError,
  connect,
  filter,
  ignoreElements,
  map,
  merge,
  of,
  OperatorFunction,
  pipe,
  scan,
  share,
  startWith,
  Subject,
  switchMap,
  throttleTime
} from 'rxjs';

import { expressionFieldChange } from '@issue-detail/components/IssueDialog/api';
import { usePromise } from '@issue-detail/utils/usePromise';
import { computed, ComputedRef, watch } from '@vue/composition-api';
import { from, useObservable } from '@vueuse/rxjs';

import { ActionStore, IssueDrawerActionEdit } from './action';
import { FieldsStore } from './fields';
import { fromRef } from 'common-module/src/util/fromRef';

const throttledBuffer = <T>(
  throttleTimeMs: number
): OperatorFunction<T, T[]> => {
  return pipe(
    connect((observable$) => {
      const throttleCalls$ = observable$.pipe(
        // tap((v) => console.log("in throttle", v)),
        throttleTime(throttleTimeMs, asyncScheduler, {
          leading: false,
          trailing: true
        })
        // tap(() => console.log("after throttle"))
      );

      const src$ = observable$.pipe(share());
      return merge(
        src$.pipe(ignoreElements()),
        src$.pipe(/* tap(console.log), */ buffer(throttleCalls$))
      );
    })
  );
};

export const useFormula = (
  actionStore: ActionStore,
  fieldStore: FieldsStore,
  isVisitor: ComputedRef<boolean>
) => {
  const formulaFields = computed(() =>
    fieldStore.fields.value.filter((f) => f.controlCode === 'formula')
  );

  const requestNewValue$ = new Subject<{
    filedId: number;
    changedValue: Record<string, any>;
  }>();

  const { result: formulaValues, refresh: refreshFormulaValues } = usePromise(
    (issueId: number) => {
      if (formulaFields.value.length > 0 && issueId) {
        return (
          expressionFieldChange({
            issueId: issueId,
            expressionFieldIds: formulaFields.value.map((f) => f.customFieldId),
            fieldChangeValues: {}
          }) as Promise<
            Array<{
              value: string;
              customFieldCode: string;
              customFieldId: number;
            }>
          >
        ).then((res) => res || []);
      }
      return [];
    },
    [],
    { immediate: false, clearOnRefresh: true }
  );

  watch([actionStore.action, formulaFields], ([action]) => {
    if (
      action?.type === 'Edit' &&
      !isVisitor.value &&
      formulaFields.value.length > 0
    ) {
      refreshFormulaValues(action.issueId);
    }
  });

  const requestNewValue = (
    filedId: number,
    changedValue: Record<string, any>
  ) => {
    requestNewValue$.next({
      filedId,
      changedValue
    });
  };

  const formulaNewValue = useObservable(
    fromRef(actionStore.action).pipe(
      switchMap((action) => {
        if (action?.type === 'Edit' && action.issueId) {
          return requestNewValue$.pipe(
            throttledBuffer(1000),
            filter((list) => list.length > 0),
            switchMap((list) => {
              return from(
                expressionFieldChange({
                  issueId: (actionStore.action.value as IssueDrawerActionEdit)
                    .issueId,
                  expressionFieldIds: list.map((item) => item.filedId),
                  fieldChangeValues: list.reduce(
                    (acc, item) => ({ ...acc, ...item.changedValue }),
                    {}
                  )
                }) as Promise<
                  Array<{
                    value: string;
                    customFieldCode: string;
                    customFieldId: number;
                  }>
                >
              ).pipe(catchError(() => of([])));
            }),
            scan((acc, curr) => {
              curr.forEach((item) => {
                acc[item.customFieldCode] = item.value;
                return map;
              });
              return acc;
            }, {} as Record<string, string>),
            map((obj) => cloneDeep(obj))
          );
        }
        return of({} as Record<string, string>);
      }),
      startWith({} as Record<string, string>)
    ),
    {
      initialValue: {} as Record<string, string>
    }
  );

  const formulaValueMap = computed(() => {
    if (actionStore.action.value?.type !== 'Edit') {
      return {};
    }

    if (isVisitor.value) {
      return fieldStore.fieldValues.value;
    }
    return formulaValues.value.reduce((map, item) => {
      map[item.customFieldCode] =
        formulaNewValue.value[item.customFieldCode] ?? item.value;
      return map;
    }, {} as Record<string, string>);
  });

  return { formulaValueMap, requestNewValue, formulaNewValue };
};

export type FormulaStore = ReturnType<typeof useFormula>;
